import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ScrollDirectionsEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {isShallowEqual} from '../../../helpers/reselectHelpers';
import {ConversationTimelineBoundary} from '../../../helpers/viewerHelpers';
import {ActivityVisualTypesEnum} from '../../../models/api/activityModel';
import {findPanelSequence} from '../../../services/panels';
import {AppAction} from '../../appActions';
import {AsyncMoreStatusesEnum} from '../../appTypes';
import {ActivitiesActionsEnum} from '../../data/activities/activitiesActions';
import {ConversationsActionsEnum} from '../../data/conversations/conversationsActions';
import {DataCascade} from '../../data/dataCascade';
import {computeIsEmptyInboxesSelection} from '../inboxes/parameters/inboxesParametersHelpers';
import {PanelCancelTokenId} from '../panelCancelTokenId';
import {activityViewersReducer} from './activity/activityViewersReducer';
import {
  isViewerPanelPageAction,
  ViewerPanelActionsEnum,
  ViewerPanelPageActionsEnum,
} from './viewerPanelActions';
import {
  ViewerPanelConversationPageState,
  ViewerPanelPagesState,
  ViewerPanelPageState,
  ViewerPanelPageTypesEnum,
  ViewerPanelState,
} from './viewerPanelState';

/*
 * Viewer.
 */

export function viewerPanelReducer(
  state: ViewerPanelState,
  action: AppAction,
  dataCascade: DataCascade,
): ViewerPanelState | undefined {
  const newPages = viewerPanelPagesReducer(state.pages, action, dataCascade);
  // If needed, remove the panel altogether.
  if (
    state.popupAppPanelId &&
    newPages.current.type === ViewerPanelPageTypesEnum.NO_CONVERSATION &&
    !state.composeCommentUid
  ) {
    return undefined;
  }

  const newState: ViewerPanelState = {
    ...state,
    pages: newPages,
    cancelTokenId: viewerPanelCancelTokenIdReducer(state.cancelTokenId, action),
    shouldForcePopout: viewerPanelShouldForcePopoutReducer(state.shouldForcePopout, action),
  };

  if (isShallowEqual(newState, state)) {
    return state;
  }

  return newState;
}

/*
 * Viewer pages.
 */

function viewerPanelPagesReducer(
  state: ViewerPanelPagesState,
  action: AppAction,
  dataCascade: DataCascade,
): ViewerPanelPagesState {
  switch (action.type) {
    // TODO: Can we simplify this?
    case ViewerPanelActionsEnum.NAVIGATED: {
      const {page} = action.payload;

      // If the current page has no content, replace it with the new page immediately.
      // - An empty P3 corresponding to a P2 that hasn't loaded yet (nothing is selected, not even 0).
      // - An empty P3 corresponding to an empty P2 that has taken over the whole P2/P3 area (with TvI feature flag).
      // - A single conversation P3 that hasn't loaded yet.
      if (
        state.current.type === ViewerPanelPageTypesEnum.NO_CONVERSATION ||
        (state.current.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION &&
          state.current.status !== AsyncStatusesEnum.LOADED)
      ) {
        return {current: page, next: undefined};
      }

      // If the next page has content, replace the current page immediately.
      // - An empty P3 that has something selected (even if it's 0, because that means P2 has loaded).
      // - A single conversation P3 that has loaded.
      // - A multi conversation P3 (since we don't need to load anything in this case).
      if (
        (page.type === ViewerPanelPageTypesEnum.NO_CONVERSATION &&
          !computeIsEmptyInboxesSelection(page.parameters.selection)) ||
        (page.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION &&
          page.status === AsyncStatusesEnum.LOADED) ||
        page.type === ViewerPanelPageTypesEnum.MULTI_CONVERSATIONS
      ) {
        return {current: page, next: undefined};
      }

      // Otherwise, put the new page on stand by.
      return {...state, next: page};
    }

    // If this is a timeout action, replace the main page if needed.
    case ViewerPanelPageActionsEnum.FETCH_CONVERSATION_GRACE_TIMEOUT: {
      const {next} = state;
      if (!next || next.uid !== action.payload.pageUid) {
        return state;
      }

      return {current: next, next: undefined};
    }

    // If this is a refetch or completion action, replace the main page.
    case ViewerPanelPageActionsEnum.REFETCH_STARTED:
    case ViewerPanelPageActionsEnum.FETCH_CONVERSATION_COMPLETED:
    case ViewerPanelPageActionsEnum.FETCH_CONVERSATION_FAILED: {
      const page = findViewerPanelPage(state, action.payload.pageUid);
      if (!page) {
        return state;
      }

      return {current: viewerPanelPageReducer(page, action, dataCascade), next: undefined};
    }

    // Otherwise, reduce the current page.
    default: {
      const current = viewerPanelPageReducer(state.current, action, dataCascade);
      if (current === state.current) {
        return state;
      }

      return {current, next: state.next};
    }
  }
}

/*
 * Viewer page.
 */

function viewerPanelPageReducer(
  state: ViewerPanelPageState,
  action: AppAction,
  dataCascade: DataCascade,
): ViewerPanelPageState {
  if (state.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION) {
    return state;
  }

  // If this is a viewer panel action, check that the page UID matches.
  const {uid, activities} = state;
  if (isViewerPanelPageAction(action) && action.payload.pageUid !== uid) {
    return state;
  }

  // Otherwise, proceed as usual.
  switch (action.type) {
    case ViewerPanelPageActionsEnum.REFETCH_STARTED:
      // If we are refetching a previously loaded page, do it in place.
      if (state.status === AsyncStatusesEnum.LOADED) {
        return state;
      }

      return {
        ...state,
        status: AsyncStatusesEnum.LOADING,
      };

    /*
     * Fetch.
     */

    case ViewerPanelPageActionsEnum.FETCH_CONVERSATION_COMPLETED: {
      const {parameters, originalConversationState} = state;
      const {topBoundary, bottomBoundary, conversationState, aroundActivity} = action.payload;

      return {
        ...state,
        status: AsyncStatusesEnum.LOADED,
        // Because we sometimes re-fetch with different parameters (see maybeRefetchConversationAroundDateAsync).
        parameters:
          parameters.activity !== aroundActivity ? {...parameters, activity: aroundActivity} : parameters,
        activities: activityViewersReducer(state, activities, action, dataCascade),
        topBoundary,
        bottomBoundary,
        moreStatusTop: computeMoreStatusForBoundary(topBoundary),
        moreStatusBottom: computeMoreStatusForBoundary(bottomBoundary),
        originalConversationState: originalConversationState ?? conversationState,
      };
    }

    case ViewerPanelPageActionsEnum.FETCH_CONVERSATION_FAILED: {
      // If we are refetching a previously loaded page, do it in place.
      if (state.status === AsyncStatusesEnum.LOADED) {
        return state;
      }

      return {
        ...state,
        status: AsyncStatusesEnum.FAILED,
      };
    }

    /*
     * Fetch more.
     */

    case ViewerPanelPageActionsEnum.FETCH_MORE_CONVERSATION:
      return {
        ...state,
        ...updatedMoreStatus(action.payload.direction, AsyncMoreStatusesEnum.LOADING),
      };

    case ViewerPanelPageActionsEnum.FETCH_MORE_CONVERSATION_COMPLETED: {
      const {direction, topBoundary, bottomBoundary} = action.payload;

      if (direction === ScrollDirectionsEnum.TOP) {
        return {
          ...state,
          activities: activityViewersReducer(state, activities, action, dataCascade),
          topBoundary,
          moreStatusTop: computeMoreStatusForBoundary(topBoundary),
        };
      }

      return {
        ...state,
        status: AsyncStatusesEnum.LOADED,
        activities: activityViewersReducer(state, activities, action, dataCascade),
        bottomBoundary,
        moreStatusBottom: computeMoreStatusForBoundary(bottomBoundary),
      };
    }

    case ViewerPanelPageActionsEnum.FETCH_MORE_CONVERSATION_FAILED:
      return {
        ...state,
        ...updatedMoreStatus(action.payload.direction, AsyncMoreStatusesEnum.ERROR),
      };

    case ViewerPanelActionsEnum.EDIT_COMMENT:
      return {
        ...state,
        editingCommentUid: action.payload.commentUid,
      };

    case ViewerPanelActionsEnum.EDIT_DRAFT_MESSAGE:
      return {
        ...state,
        draftMessageSequences: {
          ...state.draftMessageSequences,
          // FIXME: Remove this side effect from the reducer.
          [action.payload.messageUid]: findPanelSequence(),
        },
      };

    case ViewerPanelPageActionsEnum.ALL_MESSAGES_COLLAPSED:
      return {
        ...state,
        shouldExpandMessages: false,
        activities: activityViewersReducer(state, activities, action, dataCascade),
      };

    case ViewerPanelPageActionsEnum.JUMPED: {
      const {parameters} = state;
      const {jumpUid, targetActivity, sourceActivityId} = action.payload;

      return {
        ...state,
        parameters:
          parameters.activity !== targetActivity ? {...parameters, activity: targetActivity} : parameters,
        jumpUid,
        jumpSourceActivityId: sourceActivityId,
        activities: activityViewersReducer(state, activities, action, dataCascade),
      };
    }

    case ConversationsActionsEnum.MARKED_AS_SEEN: {
      const {originalConversationState} = state;
      if (!originalConversationState) {
        return state;
      }

      return {
        ...state,
        originalConversationState: {
          ...originalConversationState,
          seenAt: action.payload.seenAt,
          autoSeenAt: Math.max(action.payload.seenAt, originalConversationState.autoSeenAt ?? 0),
        },
      };
    }

    case ConversationsActionsEnum.CONVERSATION_ACCESS_DENIED:
    case ConversationsActionsEnum.CONVERSATION_DELETED:
    case ConversationsActionsEnum.CLEAR_CONVERSATION_CONTENT:
      if (state.conversationId !== action.payload.conversationId) {
        return state;
      }

      // Replace the conversation page with an empty page. In the case of a join, it'll soon be refetched.
      return {
        // We cannot generate a new UID, because that would be a side effect.
        uid: state.uid,
        type: ViewerPanelPageTypesEnum.NO_CONVERSATION,
        parameters: state.parameters,
        cellsContext: state.cellsContext,
      };

    case ActivitiesActionsEnum.ACTIVITY_DELETED:
      // Only reset `editingCommentUid` if the activity deleted was a comment
      if (action.payload.activity.visualType !== ActivityVisualTypesEnum.COMMENT) {
        return state;
      }

      // ... and it was the editing comment.
      if (action.payload.activity.commentUid !== state.editingCommentUid) {
        return state;
      }

      return {
        ...state,
        editingCommentUid: undefined,
      };

    /*
     * Message viewer actions.
     */

    default: {
      // Only update the page if the activities actually changed.
      const newActivities = activityViewersReducer(state, activities, action, dataCascade);
      if (isShallowEqual(state.activities, newActivities)) {
        return state;
      }

      return {...state, activities: newActivities};
    }
  }
}

/*
 * Cancel token.
 */

function viewerPanelCancelTokenIdReducer(
  state: PanelCancelTokenId | undefined,
  action: AppAction,
): PanelCancelTokenId | undefined {
  switch (action.type) {
    case ViewerPanelActionsEnum.NAVIGATED:
    case ViewerPanelPageActionsEnum.REFETCH_STARTED:
      return action.payload.cancelTokenId;

    default:
      return state;
  }
}

/*
 * Force Popout.
 */

function viewerPanelShouldForcePopoutReducer(state: boolean | undefined, action: AppAction) {
  switch (action.type) {
    case ViewerPanelActionsEnum.FORCE_INTERNAL_COMPOSER_POPOUT:
      return true;
    default:
      return state;
  }
}

/*
 * Helpers.
 */

function findViewerPanelPage(state: ViewerPanelPagesState, pageUid: string) {
  return [state.next, state.current].find((page) => page?.uid === pageUid);
}

/** Find the property to update based on the direction we're loading in. */
function updatedMoreStatus(
  direction: ScrollDirectionsEnum,
  status: AsyncMoreStatusesEnum,
): Partial<ViewerPanelConversationPageState> {
  switch (direction) {
    case ScrollDirectionsEnum.TOP:
      return {moreStatusTop: status};

    default:
      return {moreStatusBottom: status};
  }
}

function computeMoreStatusForBoundary(
  boundary: ConversationTimelineBoundary | undefined,
): AsyncMoreStatusesEnum {
  return boundary ? AsyncMoreStatusesEnum.HAS_MORE : AsyncMoreStatusesEnum.ALL_LOADED;
}
